import { GobernanzaAprDetailCacheService } from "@modules/gobernanza/services/gobernanza-apr-detail-cache.service";

import { computed, inject, Service, signal } from "@angular/core";

export interface IGobernanzaSession {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

const GOBERNANZA_SESSION_STORAGE_KEY = "oct-gobernanza-session";

@Service()
export class GobernanzaSessionService {
  private readonly detailCacheService = inject(GobernanzaAprDetailCacheService);
  private readonly sessionState = signal(readStoredSession());

  session = this.sessionState.asReadonly();
  accessToken = computed(() => this.sessionState()?.accessToken ?? null);
  refreshToken = computed(() => this.sessionState()?.refreshToken ?? null);
  isAuthenticated = computed(() => {
    const session = this.sessionState();
    return session ? session.expiresAt > Date.now() : false;
  });

  setSession(accessToken: string, refreshToken: string, expiresIn: number) {
    const session: IGobernanzaSession = {
      accessToken,
      refreshToken,
      expiresAt: Date.now() + expiresIn * 1000,
    };

    this.detailCacheService.clear();
    this.sessionState.set(session);
    persistSession(session);
  }

  clearSession() {
    this.detailCacheService.clear();
    this.sessionState.set(null);
    persistSession(null);
  }
}

function readStoredSession() {
  try {
    const raw = localStorage.getItem(GOBERNANZA_SESSION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as IGobernanzaSession;
  } catch {
    return null;
  }
}

function persistSession(session: IGobernanzaSession | null) {
  try {
    if (session) {
      localStorage.setItem(
        GOBERNANZA_SESSION_STORAGE_KEY,
        JSON.stringify(session)
      );
    } else {
      localStorage.removeItem(GOBERNANZA_SESSION_STORAGE_KEY);
    }
  } catch {
    return;
  }
}
